import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, Globe2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { TrackerDialog } from "@/components/admin/TrackerDialog";
import { ConfirmDelete } from "@/components/admin/shared";
import { AdminHeader, EmptyState } from "./AdminLayout";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

const Stat = ({ label, value, testId, accent }) => (
  <div className="card-tech rounded-2xl p-5">
    <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={testId} className={`mt-2 font-display text-3xl font-bold ${accent ? "text-cyan-300" : "text-white"}`}>{value}</p>
  </div>
);

const RenewalCell = ({ s }) => (
  <div className="flex flex-col items-start gap-1.5">
    <span className="whitespace-nowrap text-slate-300">{s.renewal_date ? format(parseISO(s.renewal_date), "MMM d, yyyy") : "—"}</span>
    {s.renews_soon && (
      <span data-testid={`admin-renews-soon-badge-${s.id}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-red-400/40 bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-300 shadow-[0_0_14px_rgba(255,71,87,0.35)]">
        <AlertTriangle className="h-3 w-3" /> Renews soon
      </span>
    )}
    {s.days_until_renewal !== null && s.days_until_renewal < 0 && (
      <span data-testid={`admin-overdue-badge-${s.id}`} className="rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-300">Overdue</span>
    )}
  </div>
);

export default function Tracker() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/tracker").then((r) => setData(r.data)).catch((e) => { setData({ items: [], total_monthly_revenue: 0, renews_soon_count: 0 }); err(e); });
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (dialog.item) await api.put(`/admin/tracker/${dialog.item.id}`, payload);
    else await api.post("/admin/tracker", payload);
    toast.success(dialog.item ? "Site updated" : "Site added");
    setDialog({ open: false, item: null });
    load();
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/tracker/${toDelete.id}`);
      toast.success("Site removed");
      setToDelete(null);
      load();
    } catch (e) { err(e); }
  };

  const add = (
    <button onClick={() => setDialog({ open: true, item: null })} data-testid="admin-add-tracker-button"
      className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
      <Plus className="h-4 w-4" /> Track website
    </button>
  );

  return (
    <div data-testid="admin-tracker-page">
      <AdminHeader eyebrow="// private" title="Website Tracker" action={add}>Domains, renewals, hosting and revenue for every site you own. Never shown publicly.</AdminHeader>
      {data === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Total monthly revenue" value={formatMoney(data.total_monthly_revenue)} testId="admin-total-revenue-stat" accent />
            <Stat label="Tracked sites" value={data.items.length} testId="admin-tracked-count-stat" />
            <Stat label="Renewing in 30 days" value={data.renews_soon_count} testId="admin-renews-soon-count-stat" />
          </div>
          {data.items.length === 0 ? (
            <EmptyState icon={Globe2} title="No tracked websites" text="Add a domain to start tracking renewals and revenue." testId="admin-tracker-empty-state" />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
              <table data-testid="admin-tracker-table" className="w-full min-w-[900px] text-left text-sm">
                <thead className="border-b border-white/5 font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Domain", "Registrar", "Renewal", "Hosting", "Monthly rev.", "Status", "Notes", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.items.map((s) => (
                    <tr key={s.id} data-testid={`tracker-row-${s.id}`} className="border-b border-white/5 align-top transition-colors last:border-0 hover:bg-white/[0.02]">
                      <td className="px-4 py-4 font-medium text-white">
                        {s.domain}
                        {s.source === "20i" && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span data-testid={`tracker-20i-badge-${s.id}`} title={s.twentyi?.package ? `Package ${s.twentyi.package.name} · ${s.twentyi.package.status}` : "20i domain"}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${s.twentyi?.in_20i === false ? "border-amber-400/40 text-amber-300" : "border-violet-400/40 bg-violet-500/10 text-violet-200"}`}>
                              20i{s.twentyi?.in_20i === false ? " · not found on last sync" : ""}
                            </span>
                            {s.synced_at && <span data-testid={`tracker-synced-at-${s.id}`} className="text-[10px] font-normal text-slate-500">synced {format(parseISO(s.synced_at), "MMM d, HH:mm")}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-400">{s.registrar || "—"}</td>
                      <td className="px-4 py-4"><RenewalCell s={s} /></td>
                      <td className="px-4 py-4 text-slate-400">{s.hosting || "—"}</td>
                      <td data-testid={`tracker-revenue-${s.id}`} className="px-4 py-4 font-mono-tech text-cyan-200">{formatMoney(s.monthly_revenue)}</td>
                      <td className="px-4 py-4"><span className="whitespace-nowrap rounded-full border border-white/10 px-2.5 py-0.5 text-xs text-slate-300">{s.status}</span>
                        {(s.twentyi?.package?.status || s.twentyi?.domain?.status) && <div data-testid={`tracker-20i-status-${s.id}`} className="mt-1 text-[10px] text-slate-500">20i: {[s.twentyi?.domain?.status, s.twentyi?.package?.status].filter(Boolean).join(" · ")}</div>}</td>
                      <td className="max-w-[220px] px-4 py-4 text-xs leading-relaxed text-slate-500">{s.notes || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-4 text-right">
                        <button onClick={() => setDialog({ open: true, item: s })} data-testid={`tracker-edit-button-${s.id}`} aria-label="Edit" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setToDelete(s)} data-testid={`tracker-delete-button-${s.id}`} aria-label="Delete" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <TrackerDialog open={dialog.open} item={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSave={save} />
      <ConfirmDelete target={toDelete} label={toDelete?.domain} onCancel={() => setToDelete(null)} onConfirm={remove} />
    </div>
  );
}
